import ICloudConnectModal from './ICloudConnectModal.jsx';
import CalendarSyncingResourceSetup from './CalendarSyncingResourceSetup.jsx';
import {
    setCalendarIntegrationsQuery,
    useCalendarIntegrationsQuery,
} from '../../lib/Queries';
import useDelete from '../../lib/useDelete.jsx';
import { TypeToHumanName, TypeToIcon } from '../../lib/CalendarSyncing.jsx';
import TrashIcon from '../icons/TrashIcon.jsx';
import DotDotDotMenu from '../DotDotDotMenu';
import DropdownButton from '../DropdownButton.jsx';
import GoogleIcon from '../icons/GoogleIcon.jsx';
import OutlookIcon from '../icons/OutlookIcon.jsx';
import AppleIcon from '../icons/AppleIcon.jsx';
import { useEffect, useState } from 'react';
import GoogleSocialIcon from '../icons/GoogleSocialIcon.jsx';
import BreelyIcon from '../icons/BreelyIcon.jsx';
import BreelyConnectModal from './BreelyConnectModal.jsx';
import { useCompleteStep } from '../../lib/Onboarding.js';
import Loading from '../Loading';
import ErrorMessage from '~/components/ErrorMessage';
import ErrorMessageBlock from '~/components/ErrorMessageBlock';
import InfoMessageBlock from '~/components/InfoMessageBlock';

const AccountRow = ({ account }) => {
    const { saveDelete, isDeleting } = useDelete(
        `/integrations/calendars/${account.id}/delete`,
    );

    function doDelete() {
        saveDelete().then(result => {
            setCalendarIntegrationsQuery(result);
        });
    }

    return (
        <div className="py-4 pr-6">
            <div className="flex items-center justify-between">
                <div className="flex">
                    <div className="mr-3">{TypeToIcon[account.type]}</div>
                    <div>
                        <div>{TypeToHumanName[account.type]}</div>
                        <div className="text-gray-500">{account.name}</div>
                        {!account.is_syncing_setup &&
                            typeof account.is_syncing_setup === 'boolean' && (
                                <InfoMessageBlock
                                    showIcon={true}
                                    className="mt-2"
                                >
                                    This account is not syncing to anything.
                                    <div className="mt-2">
                                        Please configure it below and edit Add
                                        Events to or Block Events From to set
                                        syncing bookings to this calendar or
                                        blocking times in Breely from it.
                                    </div>
                                </InfoMessageBlock>
                            )}
                        {!!account.error && (
                            <span className="text-red-600">
                                {account.error}. Please try removing and
                                reconnecting this account.
                            </span>
                        )}
                    </div>
                </div>
                <DotDotDotMenu
                    options={[
                        {
                            name: 'Delete',
                            isDelete: true,
                            onClick: () => {
                                doDelete();
                                return true;
                            },
                        },
                    ]}
                />
            </div>
        </div>
    );
};

const SyncingAccounts = ({ accounts, office365AuthUrl, googleAuthUrl }) => {
    const [isICloudOpen, setIsICloudOpen] = useState(false);
    const [isBreelyOpen, setIsBreelyOpen] = useState(false);

    const { complete } = useCompleteStep();

    useEffect(() => {
        if (accounts?.length > 0) {
            complete('integration-calendar');
        }
    }, [accounts?.length]);

    return (
        <div>
            <div className="panel-label">Connected Calendars</div>

            {accounts.length > 0 && (
                <div className="panel pl-6 pr-0 divide-y">
                    {accounts.map(account => (
                        <AccountRow account={account} key={account.id} />
                    ))}
                </div>
            )}
            {accounts.length === 0 && (
                <div className="panel p-4">
                    <p>You are not syncing with any calendars.</p>
                    <p className="mt-2">
                        Connect a calendar to check it for conflicts and add
                        newly booked events.
                    </p>
                </div>
            )}
            <DropdownButton
                options={[
                    {
                        name: 'Continue with Google',
                        onClick: () => (document.location.href = googleAuthUrl),
                        icon: <GoogleSocialIcon />,
                    },
                    {
                        name: 'Continue with Office 365',
                        onClick: () =>
                            (document.location.href = office365AuthUrl),
                        icon: <OutlookIcon />,
                    },
                    {
                        name: 'Continue with Apple',
                        onClick: () => setIsICloudOpen(true),
                        icon: <AppleIcon />,
                    },
                    {
                        name: 'Continue with Breely',
                        onClick: () => setIsBreelyOpen(true),
                        icon: <BreelyIcon />,
                    },
                ]}
                btnClassName="btn-light-pill mt-2"
                btnContent="Connect a Calendar"
                menuItemsClassName="w-32 left-4"
            />
            <ICloudConnectModal
                isModalShowing={isICloudOpen}
                onClose={() => setIsICloudOpen(false)}
            />
            <BreelyConnectModal
                isModalShowing={isBreelyOpen}
                onClose={() => setIsBreelyOpen(false)}
            />
        </div>
    );
};

const CalendarSyncingIntegration = ({}) => {
    const { data, isLoading } = useCalendarIntegrationsQuery();
    const { accounts, googleAuthUrl, office365AuthUrl, bookableResources, showSyncedCalendarDetails, hasMultipleUsers } =
        data || {};

    return (
        <>
            {isLoading && <Loading />}
            {!isLoading && (
                <>
                    <SyncingAccounts
                        googleAuthUrl={googleAuthUrl}
                        office365AuthUrl={office365AuthUrl}
                        accounts={accounts || []}
                    />
                    <div className="mt-6">
                        <CalendarSyncingResourceSetup
                            accounts={accounts || []}
                            bookableResources={bookableResources || []}
                            showSyncedCalendarDetails={showSyncedCalendarDetails}
                            hasMultipleUsers={hasMultipleUsers}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default CalendarSyncingIntegration;
